import React from 'react';
import PropTypes from 'prop-types';
import { isEqual, isArray } from 'lodash';
import { compose, wrapDisplayName } from 'recompose';
import { inject, observer } from 'mobx-react';

import Sentry from 'modules/sentry';
import Toast from 'modules/toast';
import apiFetch from 'modules/api-fetch';
import {
  addWSMessageListener,
  removeWSMessageListener,
} from 'modules/websocket';

import dataUpdateQueue from './data-update-queue/DataUpdateQueue';
import isFetchCacheExpired from './cache/isFetchCacheExpired';

import {
  addPendingCall,
  removePendingCall,
  getPendingCallsForEndpoint,
  awaitUntilPendingCallsComplete,
} from './PendingCalls';

// By default this adds everything directly to wrapped component as props.
// If namespace is defined, then it adds everything under that object key,
// so it will be accessible by this.props.namespace.data
const withFetch = (
  url,
  opts = {},
  namespace,
  filterByUniqId = true,
  disableCache = false,
  {
    suppressToasts = false,
    disableAPICalls = false,
    cacheExpirationDuration = undefined,
    cancelCallIfPending = true,
    retryAttempts = 1,
    failureMessage = null,
    forceConcat = false,
    listenToWSUpdates = false, // Has the shape { title<string>, doRefresh<func => bool> }
  } = {},
) => (Comp) => {
  class WithFetch extends React.Component {
    static propTypes = {
      delayLoadingMS: PropTypes.number,
      fetchCacheStore: PropTypes.shape({
        setCached: PropTypes.func.isRequired,
        concatToCached: PropTypes.func.isRequired,
        getCached: PropTypes.func.isRequired,
      }).isRequired,
    };

    static defaultProps = {
      delayLoadingMS: 0,
    };

    constructor(props) {
      super(props);

      this.state = {
        isDataLoading: true,
        dataError: null,
        delayLoadingMS: props.delayLoadingMS,
        hocFetchOpts: this.getHocFetchOpts(props),
        refreshData: this.refreshData,
        refreshDataWithCSP: this.refreshDataWithCSP,
        listenToDataArrived: this.listenToDataArrived,
        updateDataWithLocalStorage: this.updateDataWithLocalStorage,
        fromCache: false,
        url,
        query: opts.query,
        retryAttempts,
      };
    }

    componentDidMount() {
      this.makeAPIFetch();

      if (listenToWSUpdates) {
        addWSMessageListener(
          listenToWSUpdates.title,
          this.handleWSUpdate,
          this.handleWSReconnection,
        );
      }
    }

    componentDidUpdate(prevProps) {
      // See if our fetch opts have changed

      if (
        !isEqual(
          this.getHocFetchOpts(this.props),
          this.getHocFetchOpts(prevProps),
        )
      ) {
        console.devLog.info('Hoc props have changed, make new fetch...');
        this.makeAPIFetch(false);
      }
    }

    componentWillUnmount() {
      // Since this was dismounted, we will not update the state when the promise resolves
      this.isUnmounted = true;
      // Must make sure timer is removed if the component gets dismounted
      // https://facebook.github.io/react-native/docs/timers.html#timermixin
      clearTimeout(this.delayTimeout);

      if (listenToWSUpdates) {
        removeWSMessageListener(
          listenToWSUpdates.title,
          this.handleWSUpdate,
          this.handleWSReconnection,
        );
      }
    }

    getHocFetchOpts = (props) => {
      let hocFetchOpts = {};
      if (props[namespace]) {
        // eslint-disable-next-line prefer-destructuring
        hocFetchOpts = props[namespace].hocFetchOpts;
      }

      return hocFetchOpts;
    };

    handleWSUpdate = (...args) => {
      if (listenToWSUpdates.doRefresh(...args)) {
        console.devLog.info(
          `Refreshing data based on WS message '${listenToWSUpdates.title}', args: [${args}]`,
        );
        this.refreshData(false);
      }
    };

    handleWSReconnection = () => {
      console.devLog.info('Refreshing data based on WS reconnection');

      this.refreshData(false);
    };

    refreshData = async (setLoading = true, refreshForceConcat = false) =>
      this.makeAPIFetch(false, true, setLoading, refreshForceConcat);

    // This uses the Client Side Prediction (CSP) data provided to update props
    // while waiting for the updateFunction to return. It then calls refreshData
    // to ensure client data matches server.
    refreshDataWithCSP = async (
      updateFunction,
      clientSidePrediction,
      { setLoading = false } = {},
      refreshAfter = true,
    ) => {
      // Grab the current cache data in case update fails
      const originalData = this.props.fetchCacheStore.getCached(
        url,
        opts.query,
      );

      // Set CSP
      this.props.fetchCacheStore.setCached(
        url,
        opts.query,
        clientSidePrediction,
      );

      try {
        await updateFunction();
      } catch (err) {
        console.devLog.info('Err in CSP API call, time to queue it up:', err);
        if (
          err.message.includes('failed') ||
          err.message.includes('timeout') /* check if error is timeout */
        ) {
          // In this case the error was due to a timeout, so let's revert to using the queue manager.
          // We've already set the cache to match client prediction, so just add the outgoing server update
          // to the queue.
          try {
            dataUpdateQueue.addUpdateToQueue({
              url,
              query: opts.query,
              refreshDataFunction: this.refreshData,
              clientSidePrediction,
            });
          } catch (e) {
            console.devLog.info(
              'Error when adding item to data update queue.',
              e,
            );
          }

          return;
        }

        // The update failed for some other reason, undo the CSP
        this.props.fetchCacheStore.setCached(url, opts.query, originalData);

        if (!suppressToasts) {
          Toast.error(`Error loading data: ${err.toString()}`);
        }
        throw err;
      }

      if (refreshAfter) {
        await this.refreshData(setLoading);
      }
    };

    // This is similar to CSP but queues up the outgoing updates
    // while leaving the cache updated to reflect the data mutation
    updateDataWithLocalStorage = async (config) => {
      // Set data in cache as updated value (CSP)

      // Should we have a fallback in case the body isn't set??
      if (!config.body) {
        Sentry.addBreadcrumb({
          message: `No config body in updateDataWithLocalStorage for config: ${JSON.stringify(
            config,
          )}`,
          category: 'withFetch',
        });
      }
      const data = { ...config.body };

      this.props.fetchCacheStore.updateSingleEntry(url, opts.query, data);

      try {
        dataUpdateQueue.addUpdateToQueue({
          url,
          query: opts.query,
          refreshDataFunction: this.refreshData,
          config,
        });
      } catch (e) {
        console.devLog.info('Error adding item to data update queue: ', e);
      }
    };

    listenToDataArrived = (callback) => {
      this.dataArrivedCallback = callback;
    };

    callListeners = () => {
      if (this.dataArrivedCallback) {
        this.dataArrivedCallback();
      }
    };

    addBreadcrumb = (message) => {
      Sentry.addBreadcrumb({
        message,
        category: 'apiFetch',
        data: {
          url,
          opts,
          hocFetchOpts: this.getHocFetchOpts(this.props),
        },
      });
    };

    // Don't delete/comment this unused variable because it'll break apiFetch
    makeAPIFetch = async (
      isInitialFetch = true,
      refreshData = false,
      setLoading = true,
      isForcingConcat = forceConcat,
    ) => {
      // Sometimes we use withFetch only to get access to the refreshData func, in which case we never actually need the data
      if (disableAPICalls && !refreshData) {
        // console.devLog.info('Cancelling API fetch due to disable for:', url);
        return;
      }

      // We dump any additional API requests that try to fetch if we are still waiting on another to respond/timeout.
      if (this.delayTimeout) {
        Sentry.addBreadcrumb(
          'Tried to make new apiFetch while waiting for previous request, ignoring new fetch',
        );
        return;
      }

      const cachedData = this.props.fetchCacheStore.getCached(url, opts.query);

      const isLocalFetchCacheExpired = isFetchCacheExpired(
        cachedData,
        cacheExpirationDuration,
      );

      if (
        !refreshData &&
        cachedData &&
        !isLocalFetchCacheExpired
        // We need to be able to tell if the hocFetchOpts have changed...but those aren't stored with the query...hmmmm.....
      ) {
        Sentry.addBreadcrumb('Using fetch cache data');

        this.setState({
          isDataLoading: false,
          fromCache: true,
        });

        // If the cache is enabled/allowed
        if (!disableCache) {
          Sentry.addBreadcrumb('Using fetch cache data instead of API call');

          // Remember to call listeners telling them our data has arrived
          this.callListeners();
          return;
        }
      } else {
        // console.devLog.info(
        //   'Data not found, refreshing, or expired for: ',
        //   url,
        //   ' with opts:, ',
        //   opts,
        // );
        Sentry.addBreadcrumb('Fetch cache miss');
      }

      if ((!cachedData || refreshData || disableCache) && setLoading) {
        this.setState({ isDataLoading: true });
      }

      const callComplete = () => {
        if (this.isUnmounted) {
          Sentry.addBreadcrumb('Data retrieved while unmounted');
          return;
        }

        this.setState({ isDataLoading: false, fromCache: false });

        this.callListeners();
      };

      let pendingCallId;

      try {
        const pendingCallCount = getPendingCallsForEndpoint(url, opts.query);

        if (pendingCallCount.length > 0) {
          await awaitUntilPendingCallsComplete(url, opts.query);
          if (cancelCallIfPending) {
            // console.devLog.info(
            //   'Call cancelled due to pending: ',
            //   url,
            //   'namespace:',
            //   namespace,
            //   'opts query:',
            //   opts.query,
            // );
            callComplete();
            return;
          }
        }

        pendingCallId = addPendingCall(url, opts.query);

        const config = {
          url,
          method: 'GET',
          ...opts,
          ...this.getHocFetchOpts(this.props),
        };

        let data = await apiFetch(config);

        // If the server gives us a single result, wrap it in an array for consistency
        data = !isArray(data) ? [data] : data;

        // If we are refreshing data, the cache is enabled, the cache is not expired AND we are NOT forcing concat and NOT concatingExpiredCache
        // console.devLog.info('For:', url);
        // console.devLog.info('Namespace:', namespace);
        // console.devLog.info('Query:', opts.query);
        // console.devLog.info('Refreshing?', refreshData);
        // console.devLog.info('disableCache?', disableCache);
        // console.devLog.info('Is local fetch cache expired?', isLocalFetchCacheExpired);
        // console.devLog.info('forceCocnat?', forceConcat);

        // Refreshing
        // OR cache disabled
        // OR expired
        // AND NOT FORCING CONCAT
        if (
          (refreshData || disableCache || isLocalFetchCacheExpired) &&
          !isForcingConcat
        ) {
          // console.devLog.info('SET cache');
          this.props.fetchCacheStore.setCached(url, opts.query, data, {
            protectLocalData: true,
          });
        } else {
          // console.devLog.info('Concatting!');
          this.props.fetchCacheStore.concatToCached(
            url,
            opts.query,
            data,
            filterByUniqId,
            true,
          );
        }

        callComplete();
      } catch (error) {
        console.devLog.info('Error in withFetch', { error, url, opts });

        if (this.state.retryAttempts && this.state.retryAttempts > 0) {
          // Try again!
          Sentry.addBreadcrumb(
            'withFetch makeApiFetch failed, retrying the call',
          );
          this.setState(
            (prevState) => ({
              retryAttempts: prevState.retryAttempts - 1,
            }),
            this.makeAPIFetch,
          );
        } else {
          Sentry.addBreadcrumb(
            'withFetch makeApiFetch failed, no more retries',
          );
          this.setState({ dataError: error, isDataLoading: false });

          if (failureMessage) {
            Toast.error(failureMessage);
          }
        }
      } finally {
        // Make sure we remove the pending call if this one fails
        removePendingCall(pendingCallId);

        clearTimeout(this.delayTimeout);
        this.delayTimeout = null;
      }
    };

    render() {
      // If we have data in the cache, use it, otherwise pass empty array
      const cachedData = this.props.fetchCacheStore.getCached(url, opts.query);
      const data = cachedData ? cachedData.data : [];

      if (namespace) {
        const namedspacedState = { [namespace]: { data, ...this.state } };
        return <Comp {...this.props} {...namedspacedState} />;
      }
      return <Comp {...this.props} data={data} {...this.state} />;
    }
  }

  const EnhancedComponent = compose(
    inject('fetchCacheStore'),
    observer,
  )(WithFetch);

  EnhancedComponent.displayName = wrapDisplayName(
    WithFetch,
    'withStoreInjectObserver',
  );

  return EnhancedComponent;
};

export default withFetch;
